/**
 * Playlist Export Store
 * Manages state for the Export to Playlist modal and flow
 */

import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { get as apiGet, post, del } from '../lib/api-client';
import { authorizeMusicKit } from '../lib/musickit';
import type { EnrichedTrack } from '../types/track.types';

interface ConnectedPlatform {
  platform: string;
  displayName: string | null;
}

interface ExportResult {
  platform: string;
  playlistUrl: string;
  tracksAdded: number;
  tracksSkipped: number;
}

interface PlaylistExportState {
  isModalOpen: boolean;
  pendingTracks: EnrichedTrack[];
  connectedPlatforms: ConnectedPlatform[];
  isLoadingPlatforms: boolean;
  isExporting: boolean;
  isConnectingApple: boolean;
  isConnectingSpotify: boolean;
  exportResult: ExportResult | null;

  openExportModal: (tracks: EnrichedTrack[]) => void;
  closeModal: () => void;
  fetchConnectedPlatforms: () => Promise<void>;
  connectSpotify: () => Promise<{ authUrl: string } | null>;
  connectAppleMusic: () => Promise<void>;
  disconnectPlatform: (platform: string) => Promise<void>;
  exportPlaylist: (platform: string, name: string) => Promise<void>;
}

export const usePlaylistExportStore = create<PlaylistExportState>((set, getState) => ({
  isModalOpen: false,
  pendingTracks: [],
  connectedPlatforms: [],
  isLoadingPlatforms: false,
  isExporting: false,
  isConnectingApple: false,
  isConnectingSpotify: false,
  exportResult: null,

  openExportModal: (tracks) => {
    set({ isModalOpen: true, pendingTracks: tracks, exportResult: null });
    getState().fetchConnectedPlatforms();
  },

  closeModal: () => {
    set({ isModalOpen: false, pendingTracks: [], exportResult: null });
  },

  fetchConnectedPlatforms: async () => {
    set({ isLoadingPlatforms: true });
    try {
      const res = await apiGet<{ connected: ConnectedPlatform[] }>('/platform-auth/status');
      set({ connectedPlatforms: res.data?.connected ?? [] });
    } catch {
      // Non-fatal — user just won't see connected state
    } finally {
      set({ isLoadingPlatforms: false });
    }
  },

  connectSpotify: async () => {
    set({ isConnectingSpotify: true });
    try {
      const res = await apiGet<{ authUrl: string }>('/platform-auth/spotify');
      return res.data ?? null;
    } catch (err: any) {
      const message = err?.apiError?.message || err?.message || 'Failed to initiate Spotify login';
      toast.error(message);
      return null;
    } finally {
      set({ isConnectingSpotify: false });
    }
  },

  connectAppleMusic: async () => {
    set({ isConnectingApple: true });
    try {
      const tokenRes = await apiGet<{ developerToken: string }>(
        '/platform-auth/apple-music/developer-token'
      );
      const developerToken = tokenRes.data?.developerToken;
      if (!developerToken) throw new Error('Failed to get developer token');

      const musicUserToken = await authorizeMusicKit(developerToken);

      await post('/platform-auth/apple-music/connect', { musicUserToken });

      await getState().fetchConnectedPlatforms();
      toast.success('Apple Music connected!');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to connect Apple Music');
    } finally {
      set({ isConnectingApple: false });
    }
  },

  disconnectPlatform: async (platform: string) => {
    try {
      const endpoint =
        platform === 'spotify' ? '/platform-auth/spotify' : '/platform-auth/apple-music';
      await del(endpoint);
      await getState().fetchConnectedPlatforms();
      toast.success(`${platform === 'spotify' ? 'Spotify' : 'Apple Music'} disconnected`);
    } catch {
      toast.error('Failed to disconnect platform');
    }
  },

  exportPlaylist: async (platform: string, name: string) => {
    const { pendingTracks } = getState();
    set({ isExporting: true });
    try {
      const endpoint =
        platform === 'spotify' ? '/playlists/spotify' : '/playlists/apple-music';

      const res = await post<{
        playlistUrl: string;
        tracksAdded: number;
        tracksSkipped: number;
      }>(endpoint, { tracks: pendingTracks, name });

      const data = res.data!;
      set({
        exportResult: {
          platform,
          playlistUrl: data.playlistUrl,
          tracksAdded: data.tracksAdded,
          tracksSkipped: data.tracksSkipped,
        },
      });
    } catch (err: any) {
      const message =
        err?.apiError?.message || err?.message || 'Failed to export playlist';
      toast.error(message);
    } finally {
      set({ isExporting: false });
    }
  },
}));
